import {
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useBoolean, useSessionStorage } from 'usehooks-ts';

import {
  StatusIcon,
  StatusIconProps,
} from '@/components/DataDisplay/StatusIcon';
import {
  ActionButton,
  ListActionItemContainer,
} from '@/components/Form/ActionList/ActionList.styled';
import { TabEnum } from '@/components/PageForm/MoreInfoForm/components/AdultChildToggle';

import { SS_MORE_INFO_SOURCE_TAB } from '@/constants/common';
import { ROUTE } from '@/constants/routes';
import { MINOR_IDENTITY_DOCUMENTS } from '@/constants/user';

import { useDrawerOpenTracker } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectOperationUser } from '@/redux/operation';

import { DocumentStatus } from '@/types/document';
import { HolderType } from '@/types/user';

import { getDataTestId } from '@/utils/browser';

import { MinorIdentityCheckReviewDrawer } from '../drawers/MinorIdentityCheckReviewDrawer';

export function MinorIdentityCheck() {
  const { t } = useTranslate();
  const theme = useTheme();
  const navigate = useNavigate();

  const [, setMoreInfoSourceTab] = useSessionStorage<TabEnum | ''>(
    SS_MORE_INFO_SOURCE_TAB,
    '',
  );
  const operationUser = useAppSelector(selectOperationUser);
  const {
    value: isDrawerVisible,
    setTrue: showDrawer,
    setFalse: hideDrawer,
  } = useBoolean(false);

  useDrawerOpenTracker(isDrawerVisible);

  const { documents = [], holderType } = operationUser || {};
  const identityDocuments = documents.filter(document =>
    MINOR_IDENTITY_DOCUMENTS.includes(document.type),
  );
  const hasAllRequiredIdentityDocs = MINOR_IDENTITY_DOCUMENTS.every(docType =>
    identityDocuments.some(document => document.type === docType),
  );
  const hasApprovedIdentityDocs =
    hasAllRequiredIdentityDocs &&
    MINOR_IDENTITY_DOCUMENTS.every(docType =>
      identityDocuments.some(
        document =>
          document.type === docType &&
          document.status === DocumentStatus.Approved,
      ),
    );
  const isAdultKycConfirmed = operationUser?.adultConfirmKyc === true;
  const shouldReviewIdentity =
    hasAllRequiredIdentityDocs &&
    !hasApprovedIdentityDocs &&
    !isAdultKycConfirmed;

  const onClickIdentityCheck = () => {
    if (shouldReviewIdentity && holderType === HolderType.JointMinor) {
      showDrawer();
      return;
    }
    setMoreInfoSourceTab(TabEnum.Minor);
    navigate(ROUTE.VERIFY_MINOR_ID, { replace: true });
  };

  const getIconProps = (): StatusIconProps => {
    if (!hasAllRequiredIdentityDocs) {
      return { iconName: 'arrow-right', value: '' };
    }

    if (hasApprovedIdentityDocs) {
      return { value: DocumentStatus.Approved };
    }

    if (shouldReviewIdentity) {
      return { value: DocumentStatus.Pending, iconName: 'arrow-right' };
    }

    if (isAdultKycConfirmed) {
      return { value: DocumentStatus.InReview };
    }

    return { iconName: 'arrow-right', value: '' };
  };

  const isDisabledButton = useMemo(() => {
    return (
      hasApprovedIdentityDocs ||
      (hasAllRequiredIdentityDocs && isAdultKycConfirmed)
    );
  }, [
    hasAllRequiredIdentityDocs,
    hasApprovedIdentityDocs,
    isAdultKycConfirmed,
  ]);

  const statusText = useMemo(() => {
    if (hasApprovedIdentityDocs) {
      return t('moreInfo.form.identityCheck.status.completed');
    }

    if (shouldReviewIdentity) {
      return t('moreInfo.form.identityCheck.status.pendingReview');
    }

    if (hasAllRequiredIdentityDocs && isAdultKycConfirmed) {
      return t('moreInfo.form.identityCheck.status.inReview');
    }

    return t('moreInfo.form.identityCheck.status.pending');
  }, [
    hasAllRequiredIdentityDocs,
    hasApprovedIdentityDocs,
    isAdultKycConfirmed,
    shouldReviewIdentity,
    t,
  ]);

  return (
    <>
      <ListActionItemContainer
        {...getDataTestId(`list-action-item-personalDetails`)}
      >
        <ListItemText
          primary={
            <Typography variant="h5">
              {t('moreInfo.form.identityCheck.title')}
            </Typography>
          }
          secondary={
            <Typography
              variant="caption"
              {...(hasAllRequiredIdentityDocs && {
                color:
                  theme.palette.status[
                    hasApprovedIdentityDocs
                      ? DocumentStatus.Approved
                      : DocumentStatus.InReview
                  ].text,
              })}
            >
              {statusText}
            </Typography>
          }
        />
        <ListItemButton>
          <ActionButton
            {...getDataTestId(`action-button`)}
            onClick={onClickIdentityCheck}
            disabled={isDisabledButton}
          >
            <StatusIcon {...getIconProps()} />
          </ActionButton>
        </ListItemButton>
      </ListActionItemContainer>
      <MinorIdentityCheckReviewDrawer
        open={isDrawerVisible}
        onClose={hideDrawer}
        title={t('moreInfo.form.identityCheck.title')}
        onConfirmSuccess={hideDrawer}
        onRedoIdCheckSuccess={() =>
          navigate(ROUTE.VERIFY_MINOR_ID, { replace: true })
        }
      />
    </>
  );
}
